// Layout of resolved report blocks. Long text and table rows are continued,
// never truncated or shrunk to fit. Each renderer receives the resulting nodes.
const advisorPresentationBlocksApi = (() => {
  const tone = (value: string, a: any) => value === 'danger' || value === 'down' || value === 'critical' ? a.colors.red
    : value === 'success' || value === 'up' ? a.colors.green : value === 'watch' ? a.colors.amber : a.colors.purple;
  const paragraph = (value: string, a: any, note = false, color?: string, compact = false) => {
    const size = compact ? 15 : note ? 17 : 21, lineH = size * 1.4, pad = note ? compact ? 7 : 10 : 0;
    const lines = a.wrap(value, a.RIGHT - a.LEFT - pad * 2, size);
    while (lines.length) {
      a.room(lineH + pad * 2 + 14);
      const count = Math.max(1, Math.floor((a.BOTTOM - a.y - pad * 2 - 12) / lineH));
      const chunk = lines.splice(0, count);
      if (note) a.rect(a.page, a.LEFT, a.y, a.RIGHT - a.LEFT, chunk.length * lineH + pad * 2, a.page.dark ? '#704bbc' : '#eee5fb', 8);
      chunk.forEach((line: string, i: number) => a.text(a.page, line, a.LEFT + pad, a.y + pad + i * lineH, a.RIGHT - a.LEFT - pad * 2, size,
        { color: a.page.dark ? '#ffffff' : color || a.colors.ink }));
      a.y += chunk.length * lineH + pad * 2 + 10;
      if (lines.length) a.next();
    }
  };
  const table = (block: any, a: any, x = a.LEFT, width = a.RIGHT - a.LEFT) => {
    const size = block.size || (width < 800 ? 16 : 18), lh = size * 1.4, pad = block.pad ?? 7;
    const cols = block.cols || [], n = cols.length;
    if (!n) return;
    const first = n === 1 ? width : width * (block.firstWidth || (n === 2 ? .66 : n > 4 ? .23 : .43));
    const widths = cols.map((_: any, i: number) => i === 0 ? first : (width - first) / (n - 1));
    const rowLines = (row: any[], header = false, tones: string[] = []) => cols.map((_: any, i: number) => a.wrap(row[i] ?? '', widths[i] - pad * 2, size, header || i === 0 || Boolean(tones[i])));
    const headers = rowLines(cols, true);
    const headH = Math.max(...headers.map((l: any[]) => l.length)) * lh + pad * 2;
    if (headH > 160) {
      (block.rows || []).forEach((row: any[]) => cols.forEach((col: string, i: number) => paragraph(col + ': ' + (row[i] ?? ''), a)));
      return;
    }
    const drawRow = (values: string[][], height: number, header: boolean, rowIndex: number, tones: string[] = []) => {
      if (header || rowIndex % 2 === 1) a.rect(a.page, x, a.y, width, height, header ? '#e8def7' : '#ffffff');
      let left = x;
      values.forEach((lines: string[], i: number) => {
        lines.forEach((value, j) => a.text(a.page, value, left + pad, a.y + pad + j * lh, widths[i] - pad * 2, size,
          { bold: header || i === 0 || Boolean(tones[i]), color: header ? a.colors.purple : tones[i] ? tone(tones[i], a) : a.colors.ink, align: i ? 'right' : 'left' }));
        left += widths[i];
      });
      a.line(a.page, x, a.y + height, width, 0);
      a.y += height;
    };
    a.room(headH + lh + pad * 2);
    drawRow(headers, headH, true, 0);
    (block.rows || []).forEach((row: any[], index: number) => {
      const cells = rowLines(row, false, block.tones?.[index]);
      let remaining = Math.max(...cells.map((l: any[]) => l.length));
      while (remaining) {
        const fullHeight = remaining * lh + pad * 2;
        if (a.y + fullHeight > a.BOTTOM && fullHeight + headH <= a.BOTTOM - a.TOP) {
          a.next(); drawRow(headers, headH, true, 0);
        }
        if (a.y + lh + pad * 2 > a.BOTTOM) { a.next(); drawRow(headers, headH, true, 0); }
        const take = Math.max(1, Math.min(remaining, Math.floor((a.BOTTOM - a.y - pad * 2) / lh)));
        drawRow(cells.map((lines: string[]) => lines.splice(0, take)), take * lh + pad * 2, false, index, block.tones?.[index]);
        remaining = Math.max(...cells.map((l: any[]) => l.length));
        if (remaining) { a.next(); drawRow(headers, headH, true, 0); }
      }
    });
    a.y += 12;
  };
  const tiles = (rows: any[], a: any, columns = 4) => {
    for (let start = 0; start < rows.length; start += columns) {
      const chunk = rows.slice(start, start + columns), width = (a.RIGHT - a.LEFT - (chunk.length - 1) * 20) / chunk.length;
      const heights = chunk.map((r: any) => a.wrap(r.label, width - 32, 18, true).length * 25.2
        + a.wrap(r.value, width - 32, 32, true).length * 44.8 + (r.note ? a.wrap(r.note, width - 32, 15).length * 21 : 0) + 30);
      const height = Math.max(112, ...heights);
      // A long data label falls back to normal flow rather than overflowing a tile.
      if (height > a.BOTTOM - a.TOP) { chunk.forEach((r: any) => paragraph([r.label, r.value, r.note].filter(Boolean).join(' · '), a)); continue; }
      a.room(height);
      chunk.forEach((row: any, i: number) => {
        const x = a.LEFT + i * (width + 20);
        a.rect(a.page, x, a.y, width, height, '#ffffff', 8);
        let y = a.y + 12;
        y += a.text(a.page, row.label, x + 16, y, width - 32, 18, { bold: true, color: a.colors.muted }) + 5;
        y += a.text(a.page, row.value, x + 16, y, width - 32, 32, { bold: true, color: a.colors.ink }) + 5;
        if (row.note) a.text(a.page, row.note, x + 16, y, width - 32, 15, { color: tone(row.tone, a) });
      });
      a.y += height + 14;
    }
  };
  const cards = (rows: any[], a: any) => {
    const width = (a.RIGHT - a.LEFT - 24) / 2;
    for (let i = 0; i < rows.length; i += 2) {
      const chunk = rows.slice(i, i + 2);
      const heights = chunk.map((r: any) => 42 + a.wrap(r.label, width - 40, 22, true).length * 30.8
        + a.wrap(r.note || '', width - 40, 19).length * 26.6 + 36);
      const height = Math.max(148, ...heights);
      if (height > a.BOTTOM - a.TOP) { chunk.forEach((r: any) => paragraph([r.label, r.value, r.note].filter(Boolean).join('\n'), a, true)); continue; }
      a.room(height);
      chunk.forEach((row: any, j: number) => {
        const x = a.LEFT + j * (width + 24);
        a.rect(a.page, x, a.y, width, height, a.page.dark ? '#704bbc' : '#ffffff', 8);
        a.rect(a.page, x + 20, a.y + 22, 8, 8, tone(row.severity || row.tone, a), 4);
        let y = a.y + 12;
        y += a.text(a.page, row.value || '', x + 38, y, width - 58, 15, { bold: true, color: a.page.dark ? '#e6d9ff' : a.colors.muted }) + 12;
        y += a.text(a.page, row.label, x + 20, y, width - 40, 22, { bold: true }) + 10;
        a.text(a.page, row.note || '', x + 20, y, width - 40, 19);
      });
      a.y += height + 14;
    }
  };
  const named = (rows: any[], a: any) => {
    const max = Math.max(1, ...rows.map((r: any) => Math.abs(Number(r.rawValue) || 0)));
    rows.forEach((row) => {
      const labelH = Math.max(a.wrap(row.label, 800, 21, true).length * 29.4, a.wrap(row.value, 280, 21, true).length * 29.4);
      if (labelH + 24 > a.BOTTOM - a.TOP) { paragraph(row.label + ' \u00b7 ' + row.value, a); return; }
      a.room(labelH + 24);
      a.text(a.page, row.label, a.LEFT, a.y, 800, 21, { bold: true });
      a.text(a.page, row.value, a.RIGHT - 280, a.y, 280, 21, { bold: true, align: 'right' });
      a.rect(a.page, a.LEFT, a.y + labelH + 5, a.RIGHT - a.LEFT, 6, '#e6dbf7', 5);
      a.rect(a.page, a.LEFT, a.y + labelH + 5, (a.RIGHT - a.LEFT) * Math.abs(Number(row.rawValue) || 0) / max, 6, Number(row.rawValue) < 0 ? a.colors.red : a.colors.purple, 5);
      a.y += labelH + 24;
    });
  };
  // Estimate using the same wrapping rules as the table renderer, before pairing.
  const tableHeight = (b: any, a: any, width: number) => {
    const n = b.cols.length, size = b.size || (width < 800 ? 16 : 18), pad = b.pad ?? 7;
    const first = n === 1 ? width : width * (b.firstWidth || (n === 2 ? .66 : n > 4 ? .23 : .43));
    const widths = b.cols.map((_: any, i: number) => i ? (width - first) / (n - 1) : first);
    return [b.cols, ...b.rows].reduce((height: number, row: any[], index: number) => height + Math.max(...row.map((cell, i) =>
      a.wrap(cell ?? '', widths[i] - pad * 2, size, i === 0 || index === 0 || Boolean(b.tones?.[index - 1]?.[i])).length)) * size * 1.4 + pad * 2, 0) + 12;
  };
  const compactMonthly = (blocks: any[], a: any) => {
    const source = blocks.find((b) => b.kind === 'table');
    const charts = blocks.filter((b) => b.kind === 'chart');
    if (!source || !source.rows.length || source.rows.length > 12 || !charts.length || charts.length > 2
      || blocks.filter((b) => b.kind === 'table').length !== 1 || blocks.some((b) => !['chart', 'table', 'text'].includes(b.kind))) return false;
    const tables = [source.rows.slice(0, 6), source.rows.slice(6)].filter((rows) => rows.length).map((rows, index) => ({
      ...source, size: source.cols.length > 4 ? 14 : 16, pad: 4, firstWidth: source.cols.length > 4 ? .20 : .25,
      rows: rows.map((row: any[], i: number) => [`${index * 6 + i + 1}. ${row[0]}`, ...row.slice(1)]),
      tones: source.tones?.slice(index * 6, index * 6 + 6),
    }));
    const width = tables.length === 2 ? 564 : a.RIGHT - a.LEFT;
    const height = Math.max(...tables.map((b) => tableHeight(b, a, width)));
    const notes = blocks.filter((b) => b.kind === 'text' && b.text);
    const noteHeight = notes.reduce((sum, b) => sum + a.wrap(b.text, a.RIGHT - a.LEFT - 14, 15).length * 21 + 24, 0);
    const chartHeight = Math.min(235, a.BOTTOM - a.TOP - height - noteHeight - 16);
    // Very large amounts/labels retain the ordinary paginated flow.
    if (chartHeight < 155) return false;
    a.room(chartHeight + height + noteHeight + 16);
    const start = a.y, chartWidth = charts.length === 2 ? 564 : a.RIGHT - a.LEFT;
    charts.forEach((chart, i) => window.PlummeAdvisorPresentationCharts.draw({ ...chart,
      categories: source.rows.map((_: any, index: number) => String(index + 1)), numberedAxis: true, compact: true,
    }, a, a.LEFT + i * 588, start, chartWidth, chartHeight - 12));
    a.y += chartHeight + 8;
    const tableY = a.y;
    tables.forEach((b, i) => { a.y = tableY; table(b, a, a.LEFT + i * 588, width); });
    a.y = tableY + height;
    notes.forEach((b) => paragraph(b.text, a, true, undefined, true));
    return true;
  };
  const render = (blocks: any[], a: any) => {
    if (['prognose', 'resultat'].includes(a.sectionId) && compactMonthly(blocks, a)) return;
    for (let i = 0; i < blocks.length; i++) {
      const b = blocks[i];
      if (b.kind === 'overview') {
        a.room(450);
        const start = a.y, left = a.LEFT;
        window.PlummeAdvisorPresentationCharts.draw(b.chart, a, left, start, 555, 335);
        a.LEFT = 672;
        tiles(b.rows, a, 2);
        a.LEFT = left;
        a.y = Math.max(a.y, start + 365);
      } else if (b.kind === 'chart') {
        const next = blocks[i + 1];
        const width = a.RIGHT - a.LEFT;
        if (b.compact && next?.kind === 'chart' && next.compact) {
          const height = 220;
          a.room(height + 12);
          const y = a.y;
          window.PlummeAdvisorPresentationCharts.draw(b, a, a.LEFT, y, 564, height - 12);
          window.PlummeAdvisorPresentationCharts.draw(next, a, 652, y, 564, height - 12);
          a.y += height + 12;
          i++;
          continue;
        }
        const candidate = next?.kind === 'table' && next.cols.length <= 4 && next.rows.length <= 9;
        const numbered = candidate && b.variant === 'waterfall';
        const pairedTable = candidate ? { ...next, size: 16, pad: 6,
          rows: numbered ? next.rows.map((row: any[], index: number) => [`${index + 1}. ${row[0]}`, ...row.slice(1)]) : next.rows } : null;
        const pairedHeight = pairedTable ? tableHeight(pairedTable, a, 564) : Infinity;
        const paired = pairedHeight <= 370;
        const height = paired ? Math.max(280, pairedHeight) : b.compact ? 220 : 280;
        a.room(height + 12);
        const y = a.y;
        const chart = { ...b, numberedAxis: numbered && paired };
        window.PlummeAdvisorPresentationCharts.draw(chart, a, a.LEFT, y, paired ? 564 : width, height - 15);
        if (paired) { table(pairedTable, a, 652, 564); a.y = Math.max(a.y, y + height + 12); i++; }
        else a.y += height + 12;
        if (chart.axisKey) paragraph(chart.axisKey.join(' \u00b7 '), a, true);
      } else if (b.kind === 'table') table(b, a);
      else if (b.kind === 'rows' && ['kpis', 'metrics'].includes(b.sourceType)) tiles(b.rows, a);
      else if (b.kind === 'rows' && b.sourceType === 'namedList') named(a.sectionId === 'salg' ? b.rows.slice(0, 4) : b.rows, a);
      else if (b.kind === 'rows' && b.sourceType === 'actions') table({ cols: a.actionColumns, firstWidth: .55, size: 17, rows: b.rows.map((r: any) => [r.label, r.value, r.note || '']) }, a);
      else if (b.kind === 'rows' && b.sourceType === 'risks') cards(b.rows, a);
      else if (b.kind === 'rows') b.rows.forEach((r: any) => paragraph([r.label, r.value, r.note].filter(Boolean).join(' · '), a));
      else if (b.kind === 'list') b.items.forEach((item: string) => paragraph('• ' + item, a, false, undefined, a.sectionId === 'datagrunnlag'));
      else if (b.kind === 'text' && b.text) paragraph(b.text, a, Boolean(b.tone), b.tone ? tone(b.tone, a) : undefined);
    }
  };
  return { render };
})();
Object.assign(window, { PlummeAdvisorPresentationBlocks: advisorPresentationBlocksApi });
